import type {TFunction} from "i18next";
import type {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import type {PickerVariable} from "../../common/variables/VariablePicker";
import {
    getPrintoutVariableChipLabel,
    getPrintoutVariablesForType,
    type PrintoutVariableDefinition,
} from "./printoutVariables";

export interface PrintoutPickerVariable {
    key: string;
    reference: string;
    variable: PrintoutVariableDefinition;
}

const toPickerItem = (variable: PrintoutVariableDefinition): PrintoutPickerVariable => ({
    key: variable.key,
    reference: variable.key,
    variable,
});

// Catalogue strings may contain colons, so they are never i18next namespaces.
const translateCatalogText = (t: TFunction, text: string) => t(text, {nsSeparator: false});

// Offer only scopes the selected printout workflow can resolve.
export const getPrintoutPickerVariables = (
    printoutType: PrintoutType | null,
): PrintoutPickerVariable[] => getPrintoutVariablesForType(printoutType).map(variable => toPickerItem(variable));

// Slash search includes descriptions in addition to labels and raw keys.
export const printoutPickerVariableMatches = (
    item: PrintoutPickerVariable,
    query: string,
    t: TFunction,
) => {
    const needle = query.trim().toLowerCase();

    if (!needle) return true;

    return [
        translateCatalogText(t, item.variable.label),
        getPrintoutVariableChipLabel(item.reference),
        item.reference,
        translateCatalogText(t, item.variable.description),
        translateCatalogText(t, item.variable.scopeLabel),
    ].some((value) => value.toLowerCase().includes(needle));
};

export const toPrintoutPickerVariables = (
    items: PrintoutPickerVariable[],
    t: TFunction,
): PickerVariable[] => items.map((item) => {
    const label = translateCatalogText(t, item.variable.label);

    return {
        key: item.reference,
        label,
        description: translateCatalogText(t, item.variable.description),
        scope: translateCatalogText(t, item.variable.scopeLabel),
    };
});

export const printoutPickerVariablesByReference = (items: PrintoutPickerVariable[]) => (
    new Map(items.map((item) => [item.reference, item]))
);
